/**
 * Modelos de datos para todos los tipos de nodos de red
 * @module types/nodes
 */

// ============================================================================
// TIPOS BASE
// ============================================================================

export type NodeType = 'OLT' | 'NAP' | 'CIERRE' | 'CLIENTE' | 'POSTE' | 'POP'

export interface Coordinates {
  lat: number
  lng: number
}

export interface BaseNode {
  id: string
  /** Código legible por organización, ej. N-000042 */
  refCode?: string
  tipo: NodeType
  lat: number
  lng: number
  nombre?: string
  descripcion?: string
  folderId?: string
  createdAt: Date
  updatedAt: Date
  metadata?: Record<string, unknown>
}

// ============================================================================
// NAP (NETWORK ACCESS POINT)
// ============================================================================

export type TipoCajaNAP =
  | 'NAP aérea'
  | 'NAP pedestal'
  | 'NAP mural'
  | 'NAP subterránea'

export type TipoEmpalmeNAP = 'continuidad' | 'splitter'

export type TipoSplitter = 'balanceado' | 'desbalanceado'

export type RatioSplitter = '1:2' | '1:4' | '1:8' | '1:16' | '1:32' | '1:64'

export type RatioDesbalanceado = '90/10' | '85/15' | '80/20' | '70/30' | '60/40' | '50/50'

export type EstadoPuerto = 'libre' | 'ocupado' | 'reservado' | 'dañado'

export interface SplitterPuertoDesbalanceado {
  puerto: number
  porcentaje: number
  atenuacion: number
  estado: EstadoPuerto
  clienteId?: string
}

export interface SplitterInfo {
  id: string
  tipo: TipoSplitter
  configuracion: RatioSplitter
  puertosEntrada: number
  puertosSalida: number
  ocupado: number
  atenuacionBase?: number
}

export interface SplitterDesbalanceadoInfo {
  id: string
  tipo: 'desbalanceado'
  ratioConfig: RatioDesbalanceado
  puertosCascada: SplitterPuertoDesbalanceado[]
  puertosLocales: SplitterPuertoDesbalanceado[]
  atenuacionTotal: number
  descripcion?: string
}

// ============================================================================
// SPLITTER COMPLETO CON PUERTOS MAPEADOS
// ============================================================================

/**
 * Nivel del splitter en la red
 * - 1: Primer nivel (en Cierre/Mufa) - salidas van a cables de distribución
 * - 2: Segundo nivel (en NAP) - salidas van a puertos físicos para clientes
 */
export type NivelSplitter = 1 | 2

/**
 * Estado de un puerto de salida del splitter
 */
export type EstadoPuertoSplitter = 'libre' | 'ocupado' | 'reservado' | 'fallido'

/**
 * Tipo de destino de una salida de splitter
 */
export type TipoDestinoSplitter =
  | 'empalme_cable'   // Fusión a fibra de cable de distribución
  | 'puerto_nap'      // Puerto físico de caja NAP
  | 'cliente_directo' // Drop directo a ONT
  | 'splitter_hijo'   // Cascada: esta salida alimenta la entrada de otro splitter

/**
 * Puerto de salida del splitter con mapeo completo
 */
export interface SplitterPortSalida {
  numero: number
  colorPigtail: string // Color según TIA-598
  estado: EstadoPuertoSplitter
  atenuacion: number // dB específico de este puerto (para desbalanceados)

  // Destino de la salida
  destino?: {
    tipo: TipoDestinoSplitter
    // Para empalme_cable
    cableId?: string
    bufferNum?: number
    fibraNum?: number
    colorFibra?: string
    // Para puerto_nap
    puertoNapNum?: number
    // Para cliente_directo o puerto_nap con cliente asignado
    clienteOntId?: string
    nombreCliente?: string
    // Para splitter_hijo (cascada)
    splitterDestinoId?: string
  }
}

/**
 * Entrada del splitter (fibra que alimenta)
 */
export interface SplitterEntrada {
  cableId: string
  bufferNum: number
  fibraNum: number
  colorBuffer: string
  colorFibra: string
  // Estado de la fibra de entrada después del sangrado
  estadoFibraOrigen: 'derivado' // La fibra fue cortada para alimentar el splitter
}

/**
 * Splitter completo con cableado de entrada/salida mapeado
 * Este modelo representa la realidad física del sangrado de fibra
 */
export interface SplitterCompleto {
  id: string
  tipo: TipoSplitter
  ratio: RatioSplitter
  ratioDesbalanceado?: RatioDesbalanceado
  nivel: NivelSplitter
  
  // Ubicación
  nodoId: string // ID del nodo (Cierre o NAP) donde está instalado
  bandeja?: number // Número de bandeja si aplica
  posicion?: number // Posición dentro de la bandeja
  
  // Conexión de entrada (fibra sangrada)
  entrada: SplitterEntrada
  
  // Fibra muerta (lado hacia abajo del corte)
  fibraMuerta?: {
    cableId: string
    bufferNum: number
    fibraNum: number
    motivo: string // Ej: "Sangrado para alimentar splitter SP-001"
  }
  
  // Puertos de salida con mapeo
  salidas: SplitterPortSalida[]
  
  // Atenuación base del splitter (para balanceados)
  atenuacionBase: number
  
  // Metadatos
  fabricante?: string
  modelo?: string
  fechaInstalacion?: Date
  notas?: string
}

export interface CajaInfoNAP {
  tipo: TipoCajaNAP
  modelo: string
  fabricante?: string
  capacidadMaxima: number
  splitters?: SplitterInfo[] // For compatibility with NodeFormModal
}

export interface CableEntradaNAP {
  cableId: string
  fibrasUsadas: number
  puertoEntrada?: string
}

export interface EmpalmeNAP {
  id: string
  ranura: number
  tipo: TipoEmpalmeNAP
  fibraEntrada: string // Formato: "CAB-01|azul|rojo"

  // Para continuidad directa
  salida?: {
    tipo: 'directo'
    fibra: string
  }

  // Para splitter
  splitter?: {
    tipo: TipoSplitter
    ratio: RatioSplitter
    potenciaEntrada?: number // dBm
    atenuacion?: number // dB por salida
    puertosActivos: number
  }
}

/**
 * Tipo de conector físico del puerto NAP
 */
export type TipoConectorNAP = 'SC/APC' | 'SC/UPC' | 'LC' | 'FC/APC' | 'FC/UPC'

/**
 * Puerto físico de una caja NAP con información completa
 * Representa un adaptador donde se conecta el drop cable del cliente
 */
export interface PuertoNAP {
  puerto: number
  tipoConector: TipoConectorNAP
  estado: EstadoPuerto
  
  // Conexión interna (de dónde viene la señal)
  conexionSplitter?: {
    splitterId: string
    puertoSplitter: number
    colorPigtail?: string
    atenuacion?: number // dB del splitter hasta este puerto
  }
  
  // Cliente asignado
  cliente?: {
    clienteId: string
    nombreCliente: string
    ontId?: string           // Número de serie o MAC de ONT
    ontModelo?: string       // Modelo de ONT
    dropCableId?: string     // ID del cable drop hacia el cliente
    paqueteServicio?: string // Ej: "100 Mbps", "Empresarial"
  }
  
  // Mediciones
  potencia?: number // dBm medido en el puerto
  potenciaMinima?: number // dBm mínimo esperado según presupuesto óptico
  potenciaMaxima?: number // dBm máximo esperado
  
  // Configuración de red
  vlan?: number
  servicio?: string
  
  // Metadatos
  fechaInstalacion?: Date
  ultimaVerificacion?: Date
  notas?: string
  
  // Legacy fields para compatibilidad
  clienteId: string | null
  fibraId: string | null
}

export interface NAP extends BaseNode {
  tipo: 'NAP'
  caja: CajaInfoNAP
  cablesEntrada: CableEntradaNAP[]
  empalmes: EmpalmeNAP[]
  puertos: PuertoNAP[]
  
  // Nuevos campos para Motor de Lógica de Conexiones
  splittersCompletos?: SplitterCompleto[]
  conexionesInternas?: string[] // IDs de ConexionInterna
  
  direccion?: string
  observaciones?: string
  estadoGeneral: 'operativo' | 'mantenimiento' | 'fuera_servicio'
}

// ============================================================================
// CIERRE (CLOSURE)
// ============================================================================

export type TipoCajaCierre =
  | 'Domo'
  | 'Tipo bote'
  | 'Horizontal'
  | 'Vertical'
  | 'Inline'

export type TipoEmpalme = 'fusión' | 'mecánico'

export interface EmpalmeCierre {
  posicion: number
  cableA: string
  fibraA: number
  colorA: string
  bufferA?: string
  cableB: string
  fibraB: number
  colorB: string
  bufferB?: string
  tipo: TipoEmpalme
  atenuacion?: number // dB
  fechaEmpalme?: Date
  tecnico?: string
  notas?: string
}

export interface BandejaCierre {
  nivel: number
  capacidad: number
  empalmes: EmpalmeCierre[]
}

export interface CableConexionCierre {
  cableId: string
  direccion: 'entrada' | 'salida' | 'paso'
}

export interface Cierre extends BaseNode {
  tipo: 'CIERRE'
  tipoCaja: TipoCajaCierre
  modelo: string
  capacidad: number
  fabricante?: string
  bandejas: BandejaCierre[]
  cables: CableConexionCierre[]
  
  // Nuevos campos para Motor de Lógica de Conexiones
  splittersCompletos?: SplitterCompleto[]
  conexionesInternas?: string[] // IDs de ConexionInterna
  
  direccion?: string
  observaciones?: string
  estadoGeneral: 'operativo' | 'mantenimiento' | 'fuera_servicio'
}

// ============================================================================
// POP / OLT (POINT OF PRESENCE)
// ============================================================================

export type TipoEquipoPOP =
  | 'OLT'
  | 'SWITCH'
  | 'ROUTER'
  | 'SPLITTER'
  | 'ODF'
  | 'DIO'

export type TecnologiaPON = 'GPON' | 'EPON' | 'XGS-PON'

export type TipoPuertoPOP = 'SFP' | 'SFP+' | 'RJ45' | 'PON'

export type EstadoPuertoPOP = 'up' | 'down' | 'disabled'

export interface SlotOLT {
  slot: number
  tarjeta: string
  puertosTotal: number
  puertosUsados: number
  tecnologia: TecnologiaPON
}

export interface PuertoPOP {
  puerto: number
  tipo: TipoPuertoPOP
  velocidad: string
  estado: EstadoPuertoPOP
  conectadoA?: string
}

export interface EquipoPOP {
  id: string
  tipo: TipoEquipoPOP
  marca: string
  modelo: string
  numeroSerie?: string
  slots?: SlotOLT[]
  puertos?: PuertoPOP[]
}

export interface EnergiaPOP {
  tipo: 'AC' | 'DC' | 'Híbrido'
  voltaje: number
  respaldo: boolean
  ups?: {
    modelo: string
    capacidad: number // VA
  }
}

export interface POP extends BaseNode {
  tipo: 'POP'
  nombre: string
  direccion: string
  equipos: EquipoPOP[]
  energia: EnergiaPOP
  observaciones?: string
  responsable?: string
}

// Alias para OLT (mismo tipo que POP)
export type OLT = POP

// ============================================================================
// CLIENTE (CUSTOMER)
// ============================================================================

export type EstadoServicio = 'activo' | 'suspendido' | 'cortado'

export interface ONT {
  marca: string
  modelo: string
  serial: string
  mac?: string
}

export interface ServicioCliente {
  plan: string
  tipoServicio?: string
  estado: EstadoServicio
  fechaInstalacion: Date
  fechaUltimoPago?: Date
  napAsignada: string
  puertoNAP: number
  ont?: ONT
}

export interface InfoTecnicaCliente {
  potenciaRx?: number // dBm
  potenciaTx?: number // dBm
  distancia?: number // metros
  atenuacion?: number // dB
  vlan?: number
}

export interface Cliente extends BaseNode {
  tipo: 'CLIENTE'
  nombre: string
  documento?: string
  direccion: string
  referencia?: string
  telefono?: string
  email?: string
  servicio: ServicioCliente
  tecnico: InfoTecnicaCliente
  observaciones?: string
  fotoCasa?: string
}
// Note: Cliente.nombre overrides BaseNode.nombre? making it required

// ============================================================================
// POSTE (POLE)
// ============================================================================

export type TipoPoste = 'madera' | 'concreto' | 'metal'

export type PropietarioPoste = 'propia' | 'alquilado' | 'compartido'

export type EstadoFisicoPoste = 'bueno' | 'regular' | 'malo'

export type TipoHerrajePoste =
  | 'grapa'
  | 'abrazadera'
  | 'gancho'
  | 'fleje'

export type TipoTensor = 'tensor' | 'rienda' | 'viento'

export type TipoInstalacionCable = 'aéreo' | 'bajante'

export interface HerrajePoste {
  tipo: TipoHerrajePoste
  cantidad: number
  material?: string
}

export interface TensorPoste {
  tipo: TipoTensor
  longitud: number // metros
  angulo?: number // grados
}

export interface CableEnPoste {
  cableId: string
  metrosEnPoste: number
  tipoInstalacion: TipoInstalacionCable
  reserva?: number // metros
}

export type TipoReservaFibra = 'bobina' | 'figura8' | 'serpentin' | 'caja'
export type PropositoReserva = 'mantenimiento' | 'extension' | 'reubicacion' | 'emergencia'

export interface ReservaFibra {
  id: string
  cableId: string
  cableNombre?: string
  metrosReserva: number
  tipo: TipoReservaFibra
  proposito: PropositoReserva
  ubicacion: 'poste' | 'cierre' | 'nap' | 'camara'
  alturaInstalacion?: number
  fechaInstalacion?: Date
  condicion: 'buena' | 'regular' | 'mala'
  notas?: string
}

export interface Poste extends BaseNode {
  tipo: 'POSTE'
  codigo?: string
  tipoPoste: TipoPoste
  altura: number // metros
  propietario: PropietarioPoste
  empresa?: string
  herrajes: HerrajePoste[]
  tensores: TensorPoste[]
  cables: CableEnPoste[]
  reservas: ReservaFibra[]
  estadoFisico: EstadoFisicoPoste
  requiereMantenimiento: boolean
  observaciones?: string
}

// ============================================================================
// UNION TYPE - Todos los nodos
// ============================================================================

export type AnyNode = NAP | Cierre | POP | Cliente | Poste

// ============================================================================
// TYPE GUARDS
// ============================================================================

export function isNAP(node: AnyNode): node is NAP {
  return node.tipo === 'NAP'
}

export function isCierre(node: AnyNode): node is Cierre {
  return node.tipo === 'CIERRE'
}

export function isPOP(node: AnyNode): node is POP {
  return node.tipo === 'POP'
}

export function isCliente(node: AnyNode): node is Cliente {
  return node.tipo === 'CLIENTE'
}

export function isPoste(node: AnyNode): node is Poste {
  return node.tipo === 'POSTE'
}
